import { useMemo } from 'react';
import fixtures from '../data/fixtures.json';
import { buildPortfolio } from '../rules/portfolio';
import type { DemoPosition, DemoStock } from '../rules/types';

const stocks = fixtures.stocks as DemoStock[];
const positions = fixtures.positions as DemoPosition[];
const byTicker = new Map(stocks.map((s) => [s.ticker, s]));

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const moneySigned = (n: number) => (n >= 0 ? '+' : '−') + money(Math.abs(n)).replace('-', '');
const pct = (f: number, digits = 1) => `${f >= 0 ? '+' : '−'}${(Math.abs(f) * 100).toFixed(digits)}%`;

export default function Portfolio({
  onOpenDeepDive,
}: {
  onOpenDeepDive: (ticker: string) => void;
}) {
  const p = useMemo(() => buildPortfolio(positions, byTicker), []);

  return (
    <>
      {/*
        Three figures, three DIFFERENT baselines — labelled explicitly, because conflating them
        is the exact bug that shipped in the Mac app once. Total return is against what was
        paid; today is against yesterday's close. They are not comparable and the labels say so.
      */}
      <div className="heroGrid">
        <div className="hero">
          <div className="heroLabel">Market value</div>
          <div className="heroValue">{money(p.marketValue)}</div>
          <div className="heroSub">{money(p.invested)} invested</div>
        </div>

        <div className="hero">
          <div className="heroLabel">Total return <span className="baseline">vs cost</span></div>
          <div className={`heroValue ${p.unrealizedPnL >= 0 ? 'up' : 'down'}`}>
            {moneySigned(p.unrealizedPnL)}
          </div>
          <div className={`heroSub ${p.unrealizedPnL >= 0 ? 'up' : 'down'}`}>
            {pct(p.totalReturn)}
          </div>
        </div>

        <div className="hero">
          <div className="heroLabel">Today <span className="baseline">vs yesterday</span></div>
          <div className={`heroValue ${p.dayPnL >= 0 ? 'up' : 'down'}`}>
            {moneySigned(p.dayPnL)}
          </div>
          <div className={`heroSub ${p.dayPnL >= 0 ? 'up' : 'down'}`}>
            {pct(p.dayChange, 2)}
          </div>
        </div>
      </div>

      <div className="rows">
        {p.positions.map((pos) => (
          <button className="row rowButton posRow" key={pos.ticker}
                  onClick={() => onOpenDeepDive(pos.ticker)}>
            <div className="weightCell">
              <div className="weightBar">
                <div className="weightFill" style={{ width: `${pos.weight * 100}%` }} />
              </div>
              <div className="weightPct">{(pos.weight * 100).toFixed(0)}%</div>
            </div>

            <div className="main">
              <div className="name">
                {pos.name}<span className="tk">{pos.ticker}</span>
              </div>
              <div className="why">
                {pos.shares} shares · avg {pos.averageCost.toFixed(2)} · now {pos.price.toFixed(2)}
              </div>
            </div>

            <div className="figs">
              <div className="px">{money(pos.marketValue)}</div>
              <div className={`chg ${pos.unrealizedPnL >= 0 ? 'up' : 'down'}`}>
                {moneySigned(pos.unrealizedPnL)} ({pct(pos.returnSincePurchase)})
              </div>
              <div className="meta">
                today {moneySigned(pos.dayPnL)}
              </div>
            </div>
          </button>
        ))}
      </div>

      <p className="footnote">
        Average-cost basis. “Total return” measures against what was paid; “today” measures
        against yesterday’s close — different baselines, never mixed. The portfolio’s daily
        percentage is computed from its own reconstructed value yesterday, not as an average of
        the rows, which would be a different number whenever position sizes differ.
      </p>
    </>
  );
}
